import { Component, OnInit } from '@angular/core';
import { HisNotificationService, HisNavigationService, HisBaseGlobalsService, DdEntryMerged } from 'hisng';
import { ActivatedRoute, Router, UrlSegment } from '@angular/router';
import { BusinesstripApplication } from '../model/businesstripapplication';
import { BusinesstripApplicationService } from '../service/businesstripapplication.service';
import { forkJoin, Observable } from 'rxjs';
import { ErpTravelManagementValueService } from '../../shared/service/travelmanagementvalue.service';
import { ErpCountryValue } from 'app/rm/erp/common/person/model/person-values';
import { Optional } from 'app/rm/erp/common/utils/optional';
import { Document } from 'app/rm/erp/common/document/model/document';
import { Location } from '@angular/common';
import { Transportation } from '../model/transportation';
import { TravelItinerary } from '../model/travelitinerary';
import { Assets } from '../model/assets';
import { TravelData } from '../model/traveldata';
import { EstimatedCosts } from '../model/estimatedcosts';
import { TravelAbroad } from '../model/travelabroad';
import { Financing } from '../model/financing';
import { ReimbursementCosts } from '../model/reimbursementcosts';
import { BankDetails } from '../model/bankdetails';
import { A1Bescheinigung } from '../model/a1bescheinigung';

@Component( {
    selector: 'erp-businesstripapplication-form',
    templateUrl: './businesstripapplication-form.component.html'
} )
export class BusinesstripApplicationFormComponent implements OnInit {
    private defaultBusinesstripApplication: BusinesstripApplication = new BusinesstripApplication();
    public businesstripApplication: BusinesstripApplication = new BusinesstripApplication();

    public tabviewDisabled = true;

    public hisDdKey = new DdEntryMerged();

    display = false;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly location: Location,
        private readonly hisBaseGlobalsService: HisBaseGlobalsService,
        private readonly notificationService: HisNotificationService,
        private readonly businesstripApplicationService: BusinesstripApplicationService,
        private readonly erpTravelManagementValueService: ErpTravelManagementValueService,
        private readonly navigationService: HisNavigationService,
    ) {
    }

    ngOnInit(): void {

        let createOrEditPath = this.getPathFromUrl();

        // Default_Country_Code_Germany = '000'
        let defaultErpCountryValue$: Observable<ErpCountryValue> =
            this.erpTravelManagementValueService.getSpecificErpTravelManagementValueByUniqueName( 'erpCountry', '000' );

        switch ( createOrEditPath ) {
            case 'create':
                defaultErpCountryValue$.subscribe( {
                    next: ( defaultErpCountryValue ) => {
                        if ( this.defaultBusinesstripApplication.travelData && this.businesstripApplication.travelData ) {
                            this.defaultBusinesstripApplication.travelData.countrySelected = defaultErpCountryValue;
                            this.businesstripApplication.travelData.countrySelected =
                                { ...this.defaultBusinesstripApplication.travelData.countrySelected };
                            this.tabviewDisabled = false;
                        }
                    }
                } );
                break;

            case 'edit':
                let tempBusinesstripApplicationId: number | undefined = this.getBusinesstripApplicationIdFromUrlIfPresent();

                if ( tempBusinesstripApplicationId !== undefined ) {
                    let newBusinesstripApplication$: Observable<BusinesstripApplication> =
                        this.businesstripApplicationService.getBusinesstripApplicationById( tempBusinesstripApplicationId );
                    forkJoin( [defaultErpCountryValue$, newBusinesstripApplication$] ).subscribe( {
                        next: ( [defaultErpCountryValue, newBusinesstripApplication] ) => {
                            if ( this.defaultBusinesstripApplication.travelData ) {
                                this.defaultBusinesstripApplication.travelData.countrySelected = defaultErpCountryValue;
                                this.businesstripApplication = newBusinesstripApplication;
                                if ( this.businesstripApplication.travelData ) {
                                    if ( this.businesstripApplication.travelData.countrySelected === undefined ) {
                                        this.businesstripApplication.travelData.countrySelected =
                                            { ...this.defaultBusinesstripApplication.travelData.countrySelected };
                                    }
                                }
                                this.tabviewDisabled = false;
                            }
                        },
                        error: () => {
                            this.navigationService.navigateToAccessDenied();
                        }
                    } );
                }
                break;
        }
    }

    public getHisDdKeyHidden( key: string ): boolean {
        this.hisDdKey = this.hisBaseGlobalsService.getDd( key );
        if ( this.hisDdKey !== null && this.hisDdKey.hidden === true ) {
            return false;
        }
        return true;
    }

    private getPathFromUrl(): string {
        let createOrEditUrlPath = '';
        this.route.url.subscribe( ( urlSegments: UrlSegment[] ) => {
            Optional.ofNullable( urlSegments[1] )
                .ifPresent(
                    ( urlSegment: UrlSegment ) => {
                        createOrEditUrlPath = urlSegment.path;
                    } );
        } );
        return createOrEditUrlPath;
    }

    private getBusinesstripApplicationIdFromUrlIfPresent(): number | undefined {
        let tempBusinesstripApplicationId: number | undefined;
        this.route.paramMap.subscribe( {
            next: ( paramMap ) => {
                Optional.ofNullable( paramMap )
                    .map( ( m ) => m.get( 'id' ) )
                    .map( ( id ) => parseInt( id, 10 ) )
                    .ifPresent( ( id ) => { tempBusinesstripApplicationId = id; } );
            }
        } );
        return tempBusinesstripApplicationId;
    }

    resetBusinesstripApplication(): void {
        if ( this.businesstripApplication.id !== undefined ) {
            this.businesstripApplicationService.getBusinesstripApplicationById( this.businesstripApplication.id ).subscribe( {
                next: ( businesstripApplicationCreated ) => {
                    this.notificationService.successI18n( 'rm.erp.travelmanagement.businesstripapplication.application.reset.success', 5000 );
                    this.businesstripApplication = businesstripApplicationCreated;
                    this.tabviewDisabled = false;
                }
            } );
        } else {
            this.notificationService.successI18n( 'rm.erp.travelmanagement.businesstripapplication.application.reset.success', 5000 );
            this.businesstripApplication = new BusinesstripApplication();
            this.businesstripApplication.travelData.countrySelected = this.defaultBusinesstripApplication.travelData.countrySelected;
        }
    }

    saveBusinesstripApplication(): void {
        this.notificationService.clear();
        this.businesstripApplicationService.
            saveBusinesstripApplication( this.businesstripApplication ).subscribe( {
                next: ( businesstripApplicationCreated ) => {
                    if ( businesstripApplicationCreated.id !== null ) {
                        this.notificationService.successI18n( 'rm.erp.travelmanagement.businesstripapplication.application.save.success', 5000 );
                        this.businesstripApplication = businesstripApplicationCreated;
                        this.location.replaceState( `/businesstripapplication/edit/${this.businesstripApplication.id}` );
                        this.tabviewDisabled = false;
                    }
                }
            } );
    }

    overviewBusinesstripApplication(): void {
        this.notificationService.clear();
        this.businesstripApplicationService.
            saveBusinesstripApplication( this.businesstripApplication ).
            subscribe( {
                complete: () => {
                    this.router.navigateByUrl( `/businesstripapplication/overview/${this.businesstripApplication.id}`,
                        { state: this.businesstripApplication } );
                },
                next: ( savedBusinesstripApplication ) => {
                    if ( savedBusinesstripApplication.id !== undefined ) {
                        this.businesstripApplication = savedBusinesstripApplication;
                    }
                }
            } );
    }

    onTravelDataChange( changedTravelData: TravelData ): void {
        this.businesstripApplication.travelData = changedTravelData;
    }

    onTransportationChange( changedTransportation: Transportation ): void {
        this.businesstripApplication.transportation = changedTransportation;
    }

    onAssetsChange( changedAssets: Assets ): void {
        this.businesstripApplication.assets = changedAssets;
    }

    onTravelItineraryChange( listTravelitinerary: TravelItinerary[] ): void {
        this.businesstripApplication.listTravelitinerary = listTravelitinerary;
    }

    onDocumentsChange( documents: Document[] ): void {
        this.businesstripApplication.documents = documents;
    }

    onEstimatedCostsChange( estimatedCosts: EstimatedCosts ): void {
        this.businesstripApplication.estimatedCosts = estimatedCosts;
    }

    onTravelAbroadChange( listTravelAbroad: TravelAbroad[] ): void {
        this.businesstripApplication.listTravelAbroad = listTravelAbroad;
    }

    onA1BescheinigungChange( a1Bescheinigung: A1Bescheinigung ): void {
        this.businesstripApplication.a1Bescheinigung = a1Bescheinigung;
    }

    onFinancingChange( listFinancing: Financing[] ): void {
        this.businesstripApplication.listFinancing = listFinancing;
    }

    onReimbursementCostsChange( reimbursementCosts: ReimbursementCosts ): void {
        this.businesstripApplication.reimbursementCosts = reimbursementCosts;
    }

    onBankDetailsChange( bankDetails: BankDetails ): void {
        this.businesstripApplication.bankDetails = bankDetails;
    }

    dummySaveAndVerify(): void {
        this.display = true;
    }

}
